import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import config from '../config';
import {
  AdminBreadcrumbs,
  AdminSectionHeading,
  AdminStatCard,
} from './components/AdminUiPrimitives';
import {
  AdminDataGrid,
  AdminFilterBar,
  AdminPageHeader,
  DataGridColumn,
  FilterConfig,
  HeaderAction,
  SortDirection,
} from './components/AdminMasterDetail';
import { IconBook, IconCheckCircle, IconGeneral, IconRelationships } from './components/AdminIcons';

interface OrganizationSummary {
  organizationId: number;
  organizationName: string;
  organizationSlug: string;
  isDefault?: boolean;
}

interface OrganizationMember {
  membershipId: number;
  userId: number;
  auth0Sub: string;
  email: string;
  status: string;
  isDefault: boolean;
  roles: string[];
}

interface OrganizationMembersGroup {
  organizationId: number;
  organizationName: string;
  organizationSlug: string;
  members: OrganizationMember[];
}

interface PlatformUserRow {
  id: number;
  email: string;
  organization: string;
  roles: string;
  status: string;
  isManager: boolean;
  organizationId: number;
  member: OrganizationMember;
}

type SortKey = 'email' | 'organization' | 'roles' | 'status';

const DEFAULT_PAGE_SIZE = 12;

const normalizeRole = (value: string): string => value.trim();
const isManagerRole = (roles: string[]): boolean => roles.some((role) => {
  const normalized = normalizeRole(role).toLowerCase();
  return ['owner', 'principal', 'staff', 'manager'].includes(normalized);
});

const ensureRoleList = (roles: string[] | undefined): string[] =>
  Array.isArray(roles) ? roles.filter((role) => role && role.trim().length > 0).map(normalizeRole) : [];

const SuperAdminPlatformUsers: React.FC = () => {
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const apiBaseUrl = `${config.apiBaseUrl}/api/organizations`;

  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [membersByOrganization, setMembersByOrganization] = useState<Record<number, OrganizationMember[]>>({});
  const [searchValue, setSearchValue] = useState('');
  const [organizationFilter, setOrganizationFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState<SortKey>('email');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const loadInFlightRef = useRef(false);

  const isSmallScreen = viewportWidth < 768;

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const clearMessages = useCallback(() => {
    setError('');
    setNotice('');
  }, []);

  const getManagementToken = useCallback(async (): Promise<string> => {
    const tokenResponse = await getAccessTokenSilently().catch(() =>
      getAccessTokenSilently({ cacheMode: 'off' } as any)
    );

    const token =
      typeof tokenResponse === 'string'
        ? tokenResponse
        : (tokenResponse as { access_token?: string })?.access_token;

    if (!token) {
      throw new Error('Unable to get access token. Please sign in again and retry.');
    }

    return token;
  }, [getAccessTokenSilently]);

  const withAuth = useCallback(
    async (request: AxiosRequestConfig) => {
      const token = await getManagementToken();
      return axios({
        ...request,
        headers: {
          ...(request.headers ?? {}),
          Authorization: `Bearer ${token}`,
        },
      });
    },
    [getManagementToken]
  );

  const loadPlatformUsers = useCallback(async () => {
    if (loadInFlightRef.current) {
      return;
    }

    loadInFlightRef.current = true;

    try {
      setLoading(true);
      clearMessages();

      const organizationsResponse = await withAuth({
        method: 'get',
        url: `${apiBaseUrl}`,
      });

      const organizationList = (organizationsResponse.data ?? []) as OrganizationSummary[];
      setOrganizations(organizationList);

      const membersResponse = await withAuth({
        method: 'get',
        url: `${apiBaseUrl}/members`,
      });

      const groupedMembers = ((membersResponse.data ?? []) as OrganizationMembersGroup[]).map((group) => ({
        ...group,
        members: (group.members ?? []).map((member) => ({
          ...member,
          roles: ensureRoleList(member.roles),
          status: member.status || 'Active',
        })),
      }));

      const membersMap: Record<number, OrganizationMember[]> = {};
      groupedMembers.forEach((group) => {
        membersMap[group.organizationId] = group.members;
      });

      setMembersByOrganization(membersMap);
      setPage(1);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to load platform users.';
      setError(message);
      setOrganizations([]);
      setMembersByOrganization({});
    } finally {
      setLoading(false);
      loadInFlightRef.current = false;
    }
  }, [apiBaseUrl, clearMessages, withAuth]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    void loadPlatformUsers();
  }, [isAuthenticated]);

  const organizationOptions = useMemo<FilterConfig['options']>(() => {
    const options = organizations.map((organization) => ({
      value: String(organization.organizationId),
      label: organization.organizationName,
    }));

    return [{ value: 'all', label: 'All Organizations' }, ...options];
  }, [organizations]);

  const roleOptions = useMemo<FilterConfig['options']>(() => {
    const roleSet = new Set<string>();
    Object.values(membersByOrganization).forEach((members) => {
      members.forEach((member) => {
        member.roles.forEach((role) => roleSet.add(role));
      });
    });

    const options = Array.from(roleSet).sort((left, right) => left.localeCompare(right));
    return [{ value: 'all', label: 'All Roles' }, ...options.map((role) => ({ value: role, label: role }))];
  }, [membersByOrganization]);

  const statusOptions = useMemo<FilterConfig['options']>(() => {
    const statuses = new Set<string>();
    Object.values(membersByOrganization).forEach((members) => {
      members.forEach((member) => statuses.add(member.status || 'Active'));
    });

    return [{ value: 'all', label: 'All Statuses' }, ...Array.from(statuses).sort().map((status) => ({ value: status, label: status }))];
  }, [membersByOrganization]);

  const rows = useMemo<PlatformUserRow[]>(() => {
    const flattened: PlatformUserRow[] = [];

    organizations.forEach((organization) => {
      const members = membersByOrganization[organization.organizationId] ?? [];
      members.forEach((member) => {
        flattened.push({
          id: Number(`${organization.organizationId}${member.membershipId}`),
          email: member.email || member.auth0Sub || `User #${member.userId}`,
          organization: organization.organizationName,
          roles: member.roles.join(', ') || 'No role',
          status: member.status || 'Active',
          isManager: isManagerRole(member.roles),
          organizationId: organization.organizationId,
          member,
        });
      });
    });

    return flattened;
  }, [membersByOrganization, organizations]);

  const filteredRows = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesQuery =
        !query ||
        [row.email, row.organization, row.roles, row.status].join(' ').toLowerCase().includes(query);

      const matchesOrganization = organizationFilter === 'all' || String(row.organizationId) === organizationFilter;
      const matchesRole = roleFilter === 'all' || row.roles.toLowerCase().includes(roleFilter.toLowerCase());
      const matchesStatus = statusFilter === 'all' || row.status === statusFilter;

      return matchesQuery && matchesOrganization && matchesRole && matchesStatus;
    });
  }, [organizationFilter, roleFilter, rows, searchValue, statusFilter]);

  const sortedRows = useMemo(() => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    return [...filteredRows].sort((left, right) => {
      const leftValue = String(left[sortKey]).toLowerCase();
      const rightValue = String(right[sortKey]).toLowerCase();
      if (leftValue < rightValue) {
        return -1 * direction;
      }
      if (leftValue > rightValue) {
        return 1 * direction;
      }
      return 0;
    });
  }, [filteredRows, sortDirection, sortKey]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / DEFAULT_PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * DEFAULT_PAGE_SIZE;
    return sortedRows.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [page, sortedRows]);

  const columns = useMemo<DataGridColumn<PlatformUserRow>[]>(() => {
    const baseColumns: DataGridColumn<PlatformUserRow>[] = [
      {
        key: 'email',
        label: 'Email',
        sortable: true,
        width: isSmallScreen ? '40%' : '26%',
        render: (row) => (
          <div style={{ minWidth: 180 }}>
            <div style={{ fontWeight: 700, color: '#0f172a' }}>{row.email}</div>
            <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2 }}>{row.member.auth0Sub || 'Auth0 user'}</div>
          </div>
        ),
      },
      {
        key: 'organization',
        label: 'Organization',
        sortable: true,
        width: isSmallScreen ? '30%' : '22%',
        render: (row) => row.organization,
      },
      {
        key: 'roles',
        label: 'Roles',
        sortable: true,
        width: isSmallScreen ? '20%' : '20%',
        render: (row) => (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: 999,
            background: row.isManager ? '#dbeafe' : '#ecfdf5',
            color: row.isManager ? '#1d4ed8' : '#166534',
            border: `1px solid ${row.isManager ? '#bfdbfe' : '#bbf7d0'}`,
            padding: '4px 8px',
            fontSize: '0.73rem',
            fontWeight: 700,
          }}>
            {row.roles}
          </span>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        width: '12%',
        render: (row) => (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: 999,
            background: row.status === 'Active' ? '#dcfce7' : '#f3f4f6',
            color: row.status === 'Active' ? '#166534' : '#374151',
            padding: '4px 8px',
            fontSize: '0.72rem',
            fontWeight: 700,
          }}>
            {row.status}
          </span>
        ),
      },
    ];

    return baseColumns;
  }, [isSmallScreen]);

  const filterConfigs = useMemo<FilterConfig[]>(() => [
    {
      key: 'organization',
      label: 'Organization',
      value: organizationFilter,
      onChange: (value) => {
        setOrganizationFilter(value);
        setPage(1);
      },
      options: organizationOptions,
    },
    {
      key: 'role',
      label: 'Role',
      value: roleFilter,
      onChange: (value) => {
        setRoleFilter(value);
        setPage(1);
      },
      options: roleOptions,
    },
    {
      key: 'status',
      label: 'Status',
      value: statusFilter,
      onChange: (value) => {
        setStatusFilter(value);
        setPage(1);
      },
      options: statusOptions,
    },
  ], [organizationFilter, organizationOptions, roleFilter, roleOptions, statusFilter, statusOptions]);

  const handleSort = (columnKey: string) => {
    if (!['email', 'organization', 'roles', 'status'].includes(columnKey)) {
      return;
    }

    const nextKey = columnKey as SortKey;
    if (sortKey === nextKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(nextKey);
    setSortDirection('asc');
  };

  const managerCount = rows.filter((row) => row.isManager).length;
  const organizationCount = new Set(rows.map((row) => row.organizationId)).size;
  const totalUserCount = rows.length;

  const actions: HeaderAction[] = [
    {
      key: 'refresh',
      label: 'Refresh',
      variant: 'secondary',
      onClick: () => {
        void loadPlatformUsers();
      },
    },
  ];

  if (!isAuthenticated) {
    return (
      <div style={{ maxWidth: 720, margin: '32px auto', padding: 16 }}>
        <h2 style={{ marginBottom: 12 }}>Platform Users</h2>
        <p style={{ marginBottom: 16 }}>Please sign in to view the platform-wide user directory.</p>
        <button
          onClick={() => loginWithRedirect()}
          style={{
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '10px 16px',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Sign In
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', minHeight: 'calc(100vh - 260px)', display: 'grid', gap: 12 }}>
      <AdminBreadcrumbs
        items={[
          { label: 'Administration', to: '/analytics' },
          { label: 'Super Admin', to: '/super-admin' },
          { label: 'Platform Users', isCurrent: true },
        ]}
      />

      <AdminPageHeader
        title="Platform Users"
        subtitle={`${totalUserCount} memberships across ${organizationCount} organizations`}
        actions={actions}
      />

      {(error || notice) && (
        <div
          style={{
            borderRadius: 10,
            border: `1px solid ${error ? '#fecaca' : '#bfdbfe'}`,
            background: error ? '#fff1f2' : '#eff6ff',
            color: error ? '#b91c1c' : '#1d4ed8',
            padding: '10px 12px',
            fontSize: '0.84rem',
          }}
        >
          {error || notice}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 10,
        }}
      >
        <AdminStatCard label="Users" value={totalUserCount} icon={<IconBook size={15} />} />
        <AdminStatCard label="Managers" value={managerCount} icon={<IconRelationships size={15} />} accent="active" />
        <AdminStatCard label="Organizations" value={organizationCount} icon={<IconCheckCircle size={15} />} accent="archived" />
      </div>

      <AdminFilterBar
        searchValue={searchValue}
        onSearchChange={(value) => {
          setSearchValue(value);
          setPage(1);
        }}
        searchPlaceholder="Search by email, organization, role or status"
        filters={filterConfigs}
        isBusy={loading}
      />

      <div
        style={{
          border: '1px solid #dfe8f8',
          borderRadius: 12,
          background: '#fff',
          boxShadow: '0 8px 18px rgba(18, 42, 80, 0.08)',
          overflow: 'hidden',
          minHeight: 480,
        }}
      >
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #edf2fb', display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconGeneral size={14} />
          <span style={{ fontWeight: 700, color: '#173159' }}>Platform user directory</span>
        </div>

        {loading ? (
          <div style={{ padding: 24, color: '#475569' }}>Loading platform users...</div>
        ) : (
          <AdminDataGrid<PlatformUserRow>
            rows={pagedRows}
            columns={columns}
            selectedRowId={null}
            onRowClick={() => undefined}
            selectedIds={selectedIds}
            onSelectedIdsChange={setSelectedIds}
            showSelection={false}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSort={handleSort}
            page={page}
            pageSize={DEFAULT_PAGE_SIZE}
            totalCount={sortedRows.length}
            onPageChange={setPage}
            emptyMessage="No platform users found for the selected filters."
          />
        )}
      </div>
    </div>
  );
};

export default SuperAdminPlatformUsers;
